import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

export default function PautasManager({ usuario }) {
    const [pautas, setPautas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    
    // Para la vista de hitos
    const [selectedPauta, setSelectedPauta] = useState(null);
    const [hitos, setHitos] = useState([]);
    const [loadingHitos, setLoadingHitos] = useState(false);
    
    const [formData, setFormData] = useState({
        marca: '',
        modelo: '',
        descripcion: ''
    });

    const [hitoFormData, setHitoFormData] = useState({
        nombre: '',
        kilometraje: '',
        meses: '',
        tareas: ''
    });
    const [editingHitoId, setEditingHitoId] = useState(null);

    useEffect(() => {
        fetchPautas();
    }, []);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchPautas = async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth(`${API_BASE_URL}/api/mantenciones/pautas`);
            if (response.ok) {
                setPautas(await response.json());
            } else {
                setError('Error al cargar pautas');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const fetchHitos = async (pautaId) => {
        try {
            setLoadingHitos(true);
            const response = await fetchWithAuth(`${API_BASE_URL}/api/mantenciones/pautas/${pautaId}/hitos`);
            if (response.ok) {
                setHitos(await response.json());
            }
        } catch (err) {
            console.error('Error al cargar hitos:', err);
        } finally {
            setLoadingHitos(false);
        }
    };

    const handleSelectPauta = (pauta) => {
        setSelectedPauta(pauta);
        fetchHitos(pauta.id);
    };

    const handleSubmitPauta = async (e) => {
        e.preventDefault();
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/mantenciones/pautas`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            if (response.ok) {
                setShowForm(false);
                setFormData({ marca: '', modelo: '', descripcion: '' });
                fetchPautas();
            } else {
                alert('Error al guardar pauta');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const handleSubmitHito = async (e) => {
        e.preventDefault();
        if (!selectedPauta) return;

        // Convertir tareas a array simple por comas
        const tareasArray = hitoFormData.tareas.split(',').map(t => t.trim()).filter(t => t);

        try {
            const url = editingHitoId
                ? `${API_BASE_URL}/api/mantenciones/pautas/${selectedPauta.id}/hitos/${editingHitoId}`
                : `${API_BASE_URL}/api/mantenciones/pautas/${selectedPauta.id}/hitos`;
            const method = editingHitoId ? 'PUT' : 'POST';

            const response = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nombre: hitoFormData.nombre,
                    kilometraje: parseInt(hitoFormData.kilometraje),
                    meses: parseInt(hitoFormData.meses) || null,
                    tareas_requeridas: tareasArray
                })
            });
            if (response.ok) {
                setHitoFormData({ nombre: '', kilometraje: '', meses: '', tareas: '' });
                setEditingHitoId(null);
                fetchHitos(selectedPauta.id);
            } else {
                alert('Error al guardar hito');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const handleEditHito = (hito) => {
        setEditingHitoId(hito.id);
        setHitoFormData({
            nombre: hito.nombre || '',
            kilometraje: hito.kilometraje || '',
            meses: hito.meses || '',
            tareas: Array.isArray(hito.tareas_requeridas) ? hito.tareas_requeridas.join(', ') : ''
        });
    };

    const handleCancelEditHito = () => {
        setEditingHitoId(null);
        setHitoFormData({ nombre: '', kilometraje: '', meses: '', tareas: '' });
    };

    const handleDeleteHito = async (hitoId) => {
        if (!selectedPauta) return;
        if (!window.confirm('¿Está seguro de que desea eliminar este hito?')) return;

        try {
            const response = await fetchWithAuth(
                `${API_BASE_URL}/api/mantenciones/pautas/${selectedPauta.id}/hitos/${hitoId}`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                fetchHitos(selectedPauta.id);
            } else {
                alert('Error al eliminar hito');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    if (loading) return <div>Cargando pautas...</div>;
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className="manager-container">
            <div className="manager-header">
                <h2>Catálogo de Pautas de Mantención</h2>
                <button onClick={() => setShowForm(true)} className="add-button">
                    + Nueva Pauta
                </button>
            </div>

            {showForm && (
                <div className="modal">
                    <div className="standard-form">
                        <h3>Nueva Pauta</h3>
                        <form onSubmit={handleSubmitPauta}>
                            <div className="form-group">
                                <label>Marca</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.marca}
                                    onChange={(e) => setFormData({...formData, marca: e.target.value})}
                                    placeholder="Ej: Toyota"
                                />
                            </div>
                            <div className="form-group">
                                <label>Modelo</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.modelo}
                                    onChange={(e) => setFormData({...formData, modelo: e.target.value})}
                                    placeholder="Ej: Hilux"
                                />
                            </div>
                            <div className="form-group">
                                <label>Descripción</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                                    placeholder="Ej: Pauta Oficial 2024"
                                />
                            </div>
                            <div className="form-actions">
                                <button type="submit" className="btn-primary">Guardar</button>
                                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancelar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Listado de Pautas */}
            <div className="table-responsive">
                <table className="standard-table">
                    <thead>
                        <tr>
                            <th>Marca</th>
                            <th>Modelo</th>
                            <th>Descripción</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pautas.map(p => (
                            <tr key={p.id}>
                                <td>{p.marca}</td>
                                <td>{p.modelo}</td>
                                <td>{p.descripcion}</td>
                                <td>
                                    <button 
                                        onClick={() => handleSelectPauta(p)}
                                        className="btn-action btn-details"
                                        title="Ver Hitos"
                                    >
                                        ⚙️ Hitos
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {pautas.length === 0 && (
                            <tr><td colSpan="4" style={{textAlign: 'center'}}>No hay pautas registradas</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal de Hitos */}
            {selectedPauta && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <h3>Hitos de Mantención: {selectedPauta.marca} {selectedPauta.modelo}</h3>
                            <button onClick={() => setSelectedPauta(null)} className="btn-secondary" style={{ padding: '8px' }}>✕</button>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '20px', flexDirection: 'column' }}>
                            {/* Formulario de nuevo Hito */}
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <h4>{editingHitoId ? 'Editar Hito' : 'Agregar Nuevo Hito'}</h4>
                                <form onSubmit={handleSubmitHito} style={{ display: 'grid', gap: '10px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                                        <div className="form-group">
                                            <label>Nombre del Hito</label>
                                            <input type="text" required value={hitoFormData.nombre} onChange={e => setHitoFormData({...hitoFormData, nombre: e.target.value})} placeholder="Ej: Mantención 10.000 KM" />
                                        </div>
                                        <div className="form-group">
                                            <label>Kilometraje</label>
                                            <input type="number" required value={hitoFormData.kilometraje} onChange={e => setHitoFormData({...hitoFormData, kilometraje: e.target.value})} placeholder="10000" />
                                        </div>
                                        <div className="form-group">
                                            <label>Meses (Opcional)</label>
                                            <input type="number" value={hitoFormData.meses} onChange={e => setHitoFormData({...hitoFormData, meses: e.target.value})} placeholder="12" />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Tareas (separadas por coma)</label>
                                        <textarea required value={hitoFormData.tareas} onChange={e => setHitoFormData({...hitoFormData, tareas: e.target.value})} placeholder="Cambio de aceite, Filtro de polen, Revisión de frenos" rows="3"></textarea>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button type="submit" className="add-button" style={{ width: 'fit-content' }}>
                                            {editingHitoId ? '✓ Guardar Cambios' : '+ Agregar Hito'}
                                        </button>
                                        {editingHitoId && (
                                            <button type="button" onClick={handleCancelEditHito} className="btn-secondary" style={{ width: 'fit-content' }}>
                                                Cancelar Edición
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>

                            {/* Lista de hitos actuales */}
                            <div>
                                <h4>Hitos Actuales</h4>
                                {loadingHitos ? <p>Cargando hitos...</p> : (
                                    <table className="standard-table">
                                        <thead>
                                            <tr>
                                                <th>Nombre</th>
                                                <th>Kilometraje</th>
                                                <th>Meses</th>
                                                <th>Tareas</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hitos.map(h => (
                                                <tr key={h.id}>
                                                    <td>{h.nombre}</td>
                                                    <td>{h.kilometraje.toLocaleString()} KM</td>
                                                    <td>{h.meses ? `${h.meses} meses` : '-'}</td>
                                                    <td>
                                                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                                            {Array.isArray(h.tareas_requeridas) && h.tareas_requeridas.map((t, i) => (
                                                                <li key={i}>{t}</li>
                                                            ))}
                                                        </ul>
                                                    </td>
                                                    <td>
                                                        <button
                                                            onClick={() => handleEditHito(h)}
                                                            className="btn-action btn-edit"
                                                            title="Editar"
                                                            style={{ marginRight: '4px' }}
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteHito(h.id)}
                                                            className="btn-action btn-delete"
                                                            title="Eliminar"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {hitos.length === 0 && (
                                                <tr><td colSpan="5" style={{textAlign:'center'}}>No hay hitos registrados para esta pauta</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
